import React from 'react';
import { toast } from 'react-toastify';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { ExchangeRateDateEnum } from '../../../../app/shared/enums';
import { WarehouseOrderInvoicingShowDataForm } from '../../../../app/models/WarehouseOrderInvoicing';
import { PaymentMethod } from '../../../../app/models/PaymentMethod';
import { QuoteReferenceDataForm } from '../../../../app/models/Quote';
import { ExchangeRateDate } from '../../../../app/models/ExchangeRateDate';
import { ORDER_PAYMENTS_COUNT_OPTIONS } from '../../../../app/models/Order';
import { validatePaymentsDistribution } from '../../../../app/shared/paymentsDistribution';
import useOrderService from '../../../../app/services/hooks/useOrderService';

interface Props {
  warehouseOrderInvoicingDataForm: WarehouseOrderInvoicingShowDataForm;
  setWarehouseOrderInvoicingDataForm?: (form: WarehouseOrderInvoicingShowDataForm) => void;
  orderId?: number;
  paymentMethods?: PaymentMethod[];
  references?: QuoteReferenceDataForm[];
  exchangeRateDate?: ExchangeRateDate[];
  onSaved?: () => void;
}

const WarehouseOrderInvoicingShowForm: React.FC<Props> = ({
  warehouseOrderInvoicingDataForm,
  setWarehouseOrderInvoicingDataForm,
  orderId,
  paymentMethods = [],
  references = [],
  exchangeRateDate = [],
  onSaved,
}) => {
  const { updateOrderInvoicing, fetchingUpdateOrderInvoicing } = useOrderService();

  const showPrelimFixed =
    warehouseOrderInvoicingDataForm.preliminary_exchange_rate_date_reference?.id ===
    ExchangeRateDateEnum.FECHA_FIJA;

  const showFinalFixed =
    warehouseOrderInvoicingDataForm.final_exchange_rate_date_reference?.id ===
    ExchangeRateDateEnum.FECHA_FIJA;

  const canEdit = !!orderId && !!setWarehouseOrderInvoicingDataForm;

  const maxPaymentsAllowed = Number(warehouseOrderInvoicingDataForm.max_payments_allowed ?? 1);
  const paymentsCount = Number(warehouseOrderInvoicingDataForm.payments_count ?? 1);
  const showPaymentsDistribution = paymentsCount === 2;
  // Se incluye el valor vigente para no ocultarlo si la cotización bajó su tope.
  const paymentsCountOptions = ORDER_PAYMENTS_COUNT_OPTIONS.filter(
    option => option.id <= Math.max(maxPaymentsAllowed, paymentsCount)
  );

  const handlePaymentsCountChange = (value: number) => {
    if (!setWarehouseOrderInvoicingDataForm) return;
    setWarehouseOrderInvoicingDataForm({
      ...warehouseOrderInvoicingDataForm,
      payments_count: value,
      ...(value === 2
        ? {}
        : { payment_percentage_1: null, payment_percentage_2: null }),
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!setWarehouseOrderInvoicingDataForm) return;
    const { name, value } = e.target;
    setWarehouseOrderInvoicingDataForm({
      ...warehouseOrderInvoicingDataForm,
      [name]: value,
    });
  };

  const handleSelect = (name: string, value: unknown) => {
    if (!setWarehouseOrderInvoicingDataForm) return;
    setWarehouseOrderInvoicingDataForm({
      ...warehouseOrderInvoicingDataForm,
      [name]: value,
    });
  };

  const handleSave = () => {
    if (!orderId) return;

    const form = warehouseOrderInvoicingDataForm;
    const isPrelimFixed = form.preliminary_exchange_rate_date_reference?.id === ExchangeRateDateEnum.FECHA_FIJA;
    const isFinalFixed = form.final_exchange_rate_date_reference?.id === ExchangeRateDateEnum.FECHA_FIJA;

    const distributionError = validatePaymentsDistribution(
      paymentsCount,
      form.payment_percentage_1,
      form.payment_percentage_2
    );
    if (distributionError) {
      toast.error(distributionError);
      return;
    }

    updateOrderInvoicing(
      orderId,
      {
        payment_method_id: form.payment_method_id,
        reference: form.reference_id,
        days_count: form.days_count,
        payments_count: paymentsCount,
        payment_percentage_1: paymentsCount === 2 ? form.payment_percentage_1 : null,
        payment_percentage_2: paymentsCount === 2 ? form.payment_percentage_2 : null,
        preliminary_exchange_rate_date_reference: form.preliminary_exchange_rate_date_reference?.id,
        final_exchange_rate_date_reference: form.final_exchange_rate_date_reference?.id,
        ...(isPrelimFixed
          ? { preliminary_fixed_date: form.preliminary_fixed_date }
          : {
              preliminary_days: form.preliminary_days,
              preliminary_months: form.preliminary_months,
              preliminary_year: form.preliminary_year,
            }),
        ...(isFinalFixed
          ? { final_fixed_date: form.final_fixed_date }
          : {
              final_days: form.final_days,
              final_months: form.final_months,
              final_year: form.final_year,
            }),
      },
      {
        onSuccess: () => {
          toast.success('Facturación del pedido actualizada correctamente.');
          if (onSaved) onSaved();
        },
        onError: (res: { message?: string }) => {
          toast.error(res?.message || 'Error al actualizar la facturación del pedido.');
        },
      }
    );
  };

  return (
    <div className="container-fluid">
      {/* Pagos */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label>Forma de Pago</label>
          {canEdit ? (
            <SelectTwo
              name="payment_method_id"
              inputValue={warehouseOrderInvoicingDataForm.payment_method_id ?? undefined}
              options={SelectTwoMapperOptions(paymentMethods, 'id', ['name'])}
              onChange={v => handleSelect('payment_method_id', v?.value ?? null)}
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={warehouseOrderInvoicingDataForm.payment_method || ''}
              readOnly
            />
          )}
        </div>
        <div className="col-md-6">
          <label>Moneda</label>
          <input
            type="text"
            className="form-control bg-light"
            value={warehouseOrderInvoicingDataForm.currency || ''}
            readOnly
          />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label>Condición de Pago</label>
          <div className="row g-1">
            <div className="col-8">
              {canEdit ? (
                <SelectTwo
                  name="reference_id"
                  inputValue={warehouseOrderInvoicingDataForm.reference_id ?? undefined}
                  options={SelectTwoMapperOptions(references)}
                  onChange={v => handleSelect('reference_id', v?.value ?? null)}
                  placeholder="Seleccione Referencia"
                />
              ) : (
                <input
                  type="text"
                  className="form-control bg-light"
                  value={warehouseOrderInvoicingDataForm.reference || ''}
                  readOnly
                  placeholder="Referencia"
                />
              )}
            </div>
            <div className="col-4">
              <input
                type="number"
                className="form-control"
                name="days_count"
                value={warehouseOrderInvoicingDataForm.days_count ?? ''}
                onChange={handleChange}
                readOnly={!canEdit}
                placeholder="Días"
              />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <label>Fuente Tipo de Cambio</label>
          <input
            type="text"
            className="form-control bg-light"
            value={warehouseOrderInvoicingDataForm.exchange_rate_date_source || ''}
            readOnly
          />
        </div>
      </div>

      {/* Cantidad de pagos y distribución porcentual */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label>Cantidad de Pagos</label>
          {canEdit ? (
            <SelectTwo
              name="payments_count"
              inputValue={paymentsCount}
              options={SelectTwoMapperOptions(paymentsCountOptions, 'id', ['name'])}
              onChange={v => handlePaymentsCountChange(Number(v?.value ?? 1))}
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={paymentsCount === 2 ? '2 pagos' : '1 pago'}
              readOnly
            />
          )}
          {maxPaymentsAllowed < 2 && (
            <div className="form-text">
              La cotización asociada solo permite 1 pago.
            </div>
          )}
        </div>

        {showPaymentsDistribution && (
          <>
            <div className="col-md-3">
              <label>Porcentaje Pago 1 (%)</label>
              <input
                type="number"
                className="form-control"
                name="payment_percentage_1"
                value={warehouseOrderInvoicingDataForm.payment_percentage_1 ?? ''}
                onChange={handleChange}
                readOnly={!canEdit}
                min={0}
                max={100}
                step="0.01"
                placeholder="Ej: 50"
              />
            </div>
            <div className="col-md-3">
              <label>Porcentaje Pago 2 (%)</label>
              <input
                type="number"
                className="form-control"
                name="payment_percentage_2"
                value={warehouseOrderInvoicingDataForm.payment_percentage_2 ?? ''}
                onChange={handleChange}
                readOnly={!canEdit}
                min={0}
                max={100}
                step="0.01"
                placeholder="Ej: 50"
              />
            </div>
            <div className="col-12">
              <div className="form-text">
                La suma de ambos porcentajes debe ser exactamente 100%.
              </div>
            </div>
          </>
        )}
      </div>

      {/* Tipo de cambio preliminar y final (editables; el valor calculado se recibe al Guardar) */}
      <div className="row">
        {/* — Preliminar — */}
        <div className="col-lg-6 mb-3">
          <label>Tipo de Cambio Prov</label>
          {canEdit ? (
            <SelectTwo
              name="preliminary_exchange_rate_date_reference"
              inputValue={warehouseOrderInvoicingDataForm.preliminary_exchange_rate_date_reference?.id ?? undefined}
              options={SelectTwoMapperOptions(exchangeRateDate)}
              onChange={v =>
                handleSelect(
                  'preliminary_exchange_rate_date_reference',
                  v ? { id: v.value, name: v.label } : null
                )
              }
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={warehouseOrderInvoicingDataForm.preliminary_exchange_rate_date_reference_value || ''}
              readOnly
            />
          )}

          {!showPrelimFixed && (
            <div className="row g-2 mt-2">
              <div className="col-3">
                <label className="form-label">Días (Prov)</label>
                <input
                  type="number"
                  className="form-control"
                  name="preliminary_days"
                  value={warehouseOrderInvoicingDataForm.preliminary_days ?? ''}
                  onChange={handleChange}
                  readOnly={!canEdit}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Meses (Prov)</label>
                <input
                  type="number"
                  className="form-control"
                  name="preliminary_months"
                  value={warehouseOrderInvoicingDataForm.preliminary_months ?? ''}
                  onChange={handleChange}
                  readOnly={!canEdit}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Año (Prov)</label>
                <input
                  type="number"
                  className="form-control"
                  name="preliminary_year"
                  value={warehouseOrderInvoicingDataForm.preliminary_year ?? ''}
                  onChange={handleChange}
                  readOnly={!canEdit}
                />
              </div>
            </div>
          )}

          <div className="row g-2 mt-2">
            <div className="col-6">
              <label className="form-label">
                {showPrelimFixed ? 'Fecha Fija (Prov)' : 'Fecha Ref. Tipo Cambio (Prov)'}
              </label>
              {showPrelimFixed && canEdit ? (
                <input
                  type="date"
                  className="form-control"
                  name="preliminary_fixed_date"
                  value={warehouseOrderInvoicingDataForm.preliminary_fixed_date || ''}
                  onChange={handleChange}
                />
              ) : (
                <input
                  type="date"
                  className="form-control bg-light"
                  value={
                    showPrelimFixed
                      ? warehouseOrderInvoicingDataForm.preliminary_fixed_date || ''
                      : warehouseOrderInvoicingDataForm.preliminary_exchange_rate_date || ''
                  }
                  readOnly
                />
              )}
            </div>
            <div className="col-6">
              <label className="form-label">Valor Tipo Cambio (Prov)</label>
              <input
                type="text"
                className="form-control bg-light"
                value={
                  warehouseOrderInvoicingDataForm.prelim_rate !== undefined &&
                  warehouseOrderInvoicingDataForm.prelim_rate !== null
                    ? warehouseOrderInvoicingDataForm.prelim_rate
                    : ''
                }
                readOnly
              />
            </div>
          </div>
        </div>

        {/* — Final — */}
        <div className="col-lg-6 mb-3">
          <label>Tipo de Cambio Final</label>
          {canEdit ? (
            <SelectTwo
              name="final_exchange_rate_date_reference"
              inputValue={warehouseOrderInvoicingDataForm.final_exchange_rate_date_reference?.id ?? undefined}
              options={SelectTwoMapperOptions(exchangeRateDate)}
              onChange={v =>
                handleSelect(
                  'final_exchange_rate_date_reference',
                  v ? { id: v.value, name: v.label } : null
                )
              }
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={warehouseOrderInvoicingDataForm.final_exchange_rate_date_reference_value || ''}
              readOnly
            />
          )}

          {!showFinalFixed && (
            <div className="row g-2 mt-2">
              <div className="col-3">
                <label className="form-label">Días (Final)</label>
                <input
                  type="number"
                  className="form-control"
                  name="final_days"
                  value={warehouseOrderInvoicingDataForm.final_days ?? ''}
                  onChange={handleChange}
                  readOnly={!canEdit}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Meses (Final)</label>
                <input
                  type="number"
                  className="form-control"
                  name="final_months"
                  value={warehouseOrderInvoicingDataForm.final_months ?? ''}
                  onChange={handleChange}
                  readOnly={!canEdit}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Año (Final)</label>
                <input
                  type="number"
                  className="form-control"
                  name="final_year"
                  value={warehouseOrderInvoicingDataForm.final_year ?? ''}
                  onChange={handleChange}
                  readOnly={!canEdit}
                />
              </div>
            </div>
          )}

          <div className="row g-2 mt-2">
            <div className="col-6">
              <label className="form-label">
                {showFinalFixed ? 'Fecha Fija (Final)' : 'Fecha Ref. Tipo Cambio (Final)'}
              </label>
              {showFinalFixed && canEdit ? (
                <input
                  type="date"
                  className="form-control"
                  name="final_fixed_date"
                  value={warehouseOrderInvoicingDataForm.final_fixed_date || ''}
                  onChange={handleChange}
                />
              ) : (
                <input
                  type="date"
                  className="form-control bg-light"
                  value={
                    showFinalFixed
                      ? warehouseOrderInvoicingDataForm.final_fixed_date || ''
                      : warehouseOrderInvoicingDataForm.final_exchange_rate_date || ''
                  }
                  readOnly
                />
              )}
            </div>
            <div className="col-6">
              <label className="form-label">Valor Tipo Cambio (Final)</label>
              <input
                type="text"
                className="form-control bg-light"
                value={
                  warehouseOrderInvoicingDataForm.final_rate !== undefined &&
                  warehouseOrderInvoicingDataForm.final_rate !== null
                    ? warehouseOrderInvoicingDataForm.final_rate
                    : ''
                }
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="row mb-3">
          <div className="col-12 d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={fetchingUpdateOrderInvoicing}
            >
              {fetchingUpdateOrderInvoicing ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseOrderInvoicingShowForm;
